import type { DateTimePickerProps, SelectProps } from "@opensea/ui-kit"
import {
  classNames,
  DateTimePicker,
  FlexColumn,
  Select,
  SelectItem,
  TextBody,
} from "@opensea/ui-kit"
import { addMinutes } from "date-fns"
import { capitalize } from "es-toolkit/string"
import { useTranslations } from "use-intl"
import {
  EXPIRATION_IN_MINUTES,
  MAXIMUM_EXPIRATION_IN_MINUTES,
} from "../../lib/zod/expiration"
import type { ExpirationOptions } from "./constants"

type ExpirationSelectProps = {
  onChange: (value: ExpirationOptions) => void
  onCustomDateChange: (date: Date) => void
  value: ExpirationOptions
  customDate?: Date
  overrides?: {
    DateTimePicker?: Partial<DateTimePickerProps>
  }
  error?: string
} & Omit<SelectProps, "onChange" | "value" | "error">

export const ExpirationSelect = ({
  value,
  customDate,
  onCustomDateChange,
  error,
  onChange,
  className,
  overrides,
}: ExpirationSelectProps) => {
  const t = useTranslations("wallet.ExpirationSelect")
  return (
    <>
      {value === "custom" && (
        <FlexColumn className="col-span-2 items-end gap-1">
          <DateTimePicker
            disabled={{
              before: new Date(),
              after: addMinutes(new Date(), MAXIMUM_EXPIRATION_IN_MINUTES),
            }}
            error={Boolean(customDate && error)}
            onSelect={date => {
              if (date) {
                onCustomDateChange(date)
              }
            }}
            portal={false}
            selected={customDate}
            {...overrides?.DateTimePicker}
            className={classNames(
              "grid w-full grid-cols-2 gap-3 md:flex",
              overrides?.DateTimePicker?.className,
            )}
            overrides={{
              TimeInput: {
                className: "w-full md:w-fit",
              },
            }}
            placeholder={t("customExpirationPlaceholder")}
          />
          {Boolean(customDate && error) && (
            <TextBody color="error-1" size="xs">
              {error}
            </TextBody>
          )}
        </FlexColumn>
      )}

      <Select
        className={classNames("md:w-[180px]", className)}
        onValueChange={val => {
          onChange(val as ExpirationOptions)
        }}
        renderValue={val => {
          const label = Object.entries(EXPIRATION_IN_MINUTES).find(
            ([_, value]) => value.toString() === val,
          )?.[0]

          return (
            <div data-testid="expiration-select">
              {label
                ? t(label as keyof typeof EXPIRATION_IN_MINUTES)
                : capitalize(val)}
            </div>
          )
        }}
        size="md"
        value={t(value)}
      >
        {Object.keys(EXPIRATION_IN_MINUTES).map(label => (
          <SelectItem key={label} value={label}>
            {t(label as keyof typeof EXPIRATION_IN_MINUTES)}
          </SelectItem>
        ))}
      </Select>
    </>
  )
}
